import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

import { meshColors as colors } from '../styles/mesh';

function LoadingSpinner() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.bgPrimary,
        fontFamily: colors.fontSans,
        color: colors.textPrimary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <Loader2
          size={32}
          style={{ color: colors.brand, animation: 'spin 1s linear infinite' }}
        />
        <p style={{ color: colors.textSecondary, marginTop: '12px' }}>
          Loading...
        </p>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const { user, profile, loading, isAdmin, isClientManager, isPartner } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;

  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  if (!profile) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: colors.bgPrimary,
          fontFamily: colors.fontSans,
          color: colors.textPrimary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <h2 style={{ color: colors.error, marginBottom: '12px' }}>Configuration Error</h2>
          <p style={{ color: colors.textSecondary }}>
            Your account does not have a profile configured. Contact an administrator.
          </p>
        </div>
      </div>
    );
  }

  // requireAdmin allows both admin and client_manager to access dashboard routes
  if (requireAdmin && !isAdmin && !isClientManager) {
    if (isPartner) {
      return <Navigate to="/portal" replace />;
    }
    if (profile?.role === 'client_user') {
      return <Navigate to="/client" replace />;
    }
    return <Navigate to="/auth/login" replace />;
  }

  return <>{children}</>;
}

export function PartnerRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, loading, isAdmin, isPartner } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;

  if (!user || !profile) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  if (isAdmin || isPartner) {
    return <>{children}</>;
  }

  return <Navigate to="/auth/login" replace />;
}

export function ClientPortalRoute({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.loading) return <LoadingSpinner />;

  if (!auth.user || !auth.profile) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Admin can always access — used for impersonation / debugging.
  // Use actualIsAdmin so admins can access even when viewing-as-partner.
  if (auth.actualIsAdmin) {
    return <>{children}</>;
  }

  // client_user / client_manager scoping: they may only land on a
  // /client/:slug route that matches their assigned client. Otherwise
  // we redirect them to /client (which routes them to their own portal).
  // This guards UX in addition to the worker-side requireClientUserAccess.
  if (auth.isClientUser || auth.isClientManager) {
    // Pull the slug from the path — react-router doesn't expose params here.
    const match = location.pathname.match(/^\/client\/([^/]+)/);
    const slugInUrl = match ? match[1] : null;
    if (!slugInUrl) {
      // /client → ClientPortalRedirect resolves slug → URL. Let it through.
      return <>{children}</>;
    }
    // Defer the slug→id check to the page's data fetcher (which calls
    // /api/clients filtered by RLS). The worker will 403 if mismatch and
    // ClientPortalRouter shows MMP fallback. Either way the user can't see
    // OTHER clients' data — this guard just protects the URL surface.
    return <>{children}</>;
  }

  return <Navigate to="/auth/login" replace />;
}
